// @ts-nocheck
import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { InvoicesDataViewContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { InvoiceModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedCurrency from "@insite/content-library/Components/LocalizedCurrency";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import InfosBox, { InfosBoxProps } from "@store/Library/Components/Toolkit/InfosBox";
import OrderActions, { OrderActionsPresentationProps } from "@store/Library/Components/Toolkit/Order/OrderActions";
import OrderStickyAction, { OrderStickyActionProps } from "@store/Library/Components/Toolkit/Order/OrderStickyAction";
import { orderSummaryProps } from "@store/Themes/common/props/order";
import constants from "@store/constants";
import getInvoicesTermDiscount from "@store/Framework/Common/Utilities/getInvoicesTermDiscount";
import getInvoicesTotal from "@store/Framework/Common/Utilities/getInvoicesTotal";
import { getLocalizedCurrency } from "@store/Modules/client-framework/src/Common/Utilities/getLocalizedCurrency";
import { useCurrentLanguage } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { FC, useContext } from "react";
import { css } from "styled-components";

export interface InvoiceHistoryInfoProps extends WidgetProps {}

export interface InvoiceHistoryInfoStyles {
    paymentButton?: ButtonPresentationProps;
    clearButton?: ButtonPresentationProps;
    dueInfosBox?: InfosBoxProps;
    pastDueInfosBox?: InfosBoxProps;
    wrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    itemWrapper?: InjectableCss;
    totalLabel?: TypographyPresentationProps;
    totalValue?: TypographyPresentationProps;
    orderActions?: OrderActionsPresentationProps;
    orderStickyAction?: OrderStickyActionProps;
}

export const invoiceHistoryInfoStyles: InvoiceHistoryInfoStyles = {
    paymentButton: { variant: "primary" },
    clearButton: { variant: "secondary" },
    dueInfosBox: {
        type: "info",
        showIcon: true,
        size: "small",
        extendedStyles: {
            wrapper: {
                css: css`
                    height: 100%;
                `,
            },
        },
    },
    pastDueInfosBox: {
        type: "error",
        showIcon: true,
        size: "small",
        extendedStyles: {
            wrapper: {
                css: css`
                    min-height: 100%;
                `,
            },
        },
    },
    wrapper: orderSummaryProps.wrapper,
    titleWrapper: orderSummaryProps.titleWrapper,
    title: orderSummaryProps.title,
    itemWrapper: orderSummaryProps.itemWrapper,
    totalLabel: orderSummaryProps.itemLabel,
    totalValue: orderSummaryProps.itemValue,
    orderActions: {
        extendedStyles: {
            flexWrapper: {
                item: {
                    css: css`
                        width: auto;
                    `,
                },
            },
        },
    },
};

const styles = invoiceHistoryInfoStyles;

const InvoiceHistoryInfo: FC<InvoiceHistoryInfoProps> = () => {
    const dispatch = useAppDispatch();
    const { value: invoices } = useContext(InvoicesDataViewContext);
    const { totalDue = 0, totalPastDue = 0 } = useAppSelector(state => state.store.data.invoices);
    const { selectedInvoices: selectedInvoiceIds } = useAppSelector(state => state.store.pages.invoiceHistory);
    const history = useHistory();
    const currentLanguage = useCurrentLanguage();
    const currentCurrencySymbol = useAppSelector(state => state.context.session.currency?.currencySymbol);
    const invoicePaymentPageUrl = useAppSelector(
        state => getPageLinkByPageType(state, constants.pageType.invoicePayment)?.url,
    );

    if (!invoices || !currentLanguage || !currentCurrencySymbol) {
        return null;
    }

    const selectedInvoices = selectedInvoiceIds.reduce((result, id) => {
        const invoice = invoices.find(x => x.id === id);
        return invoice ? [...result, invoice] : result;
    }, [] as InvoiceModel[]);

    const selectedInvoicesAmount = getInvoicesTotal(selectedInvoices);
    const selectedInvoicesDiscountAmount = getInvoicesTermDiscount(selectedInvoices);

    const onClickPayInvoices = () => {
        if (invoicePaymentPageUrl && selectedInvoices.length > 0) {
            history.push(`${invoicePaymentPageUrl}?invoices=${selectedInvoices.map(x => x.paymentId).join(",")}`);
        }
    };

    const onClickClear = () => {
        dispatch({ type: "Store/Pages/InvoiceHistory/ClearSelectedInvoices" });
    };

    return (
        <>
            <InfosBox {...styles.dueInfosBox}>
                <Typography>
                    {translate("grimco_invoiceHistory_due")}:&nbsp;
                    <LocalizedCurrency amount={totalDue} />
                </Typography>
            </InfosBox>
            {Boolean(totalPastDue) && (
                <InfosBox {...styles.pastDueInfosBox}>
                    <Typography>
                        {translate("grimco_invoiceHistory_pastDue")}:&nbsp;
                        <LocalizedCurrency amount={totalPastDue} />
                    </Typography>
                </InfosBox>
            )}
            <StyledWrapper {...styles.wrapper}>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography {...styles.title}>{translate("Selected Invoice(s)")}</Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.itemWrapper}>
                    <Typography {...styles.totalLabel}>
                        {translate("grimco_invoice_history_selected_discount")}
                    </Typography>
                    <Typography {...styles.totalValue}>
                        <LocalizedCurrency amount={selectedInvoicesDiscountAmount} />
                    </Typography>
                </StyledWrapper>
                <StyledWrapper {...styles.itemWrapper}>
                    <Typography {...styles.totalLabel}>{translate("Total")}</Typography>
                    <Typography {...styles.totalValue}>
                        <LocalizedCurrency amount={selectedInvoicesAmount} />
                    </Typography>
                </StyledWrapper>
                <OrderActions {...styles.orderActions}>
                    <OrderStickyAction {...styles.orderStickyAction} data-test-selector="test-OrderStickyAction">
                        <Button
                            {...styles.paymentButton}
                            disabled={selectedInvoices.length === 0}
                            onClick={onClickPayInvoices}
                        >
                            {translate(
                                "Pay {0} Invoice{1}: {2}",
                                `${selectedInvoices.length}`,
                                selectedInvoices.length === 1 ? "" : "s",
                                getLocalizedCurrency({
                                    amount: selectedInvoicesAmount,
                                    language: currentLanguage,
                                    currencySymbol: currentCurrencySymbol,
                                }),
                            )}
                        </Button>
                    </OrderStickyAction>
                    <Button {...styles.clearButton} disabled={selectedInvoices.length === 0} onClick={onClickClear}>
                        {translate("Clear Selected")}
                    </Button>
                </OrderActions>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: InvoiceHistoryInfo,
    definition: {
        group: "Invoice History",
        displayName: "Informations",
        allowedContexts: ["InvoiceHistoryPage"],
        fieldDefinitions: [],
    },
};

export default widgetModule;
